import * as React from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import {
  Calendar,
  DollarSign,
  BookOpen,
  Receipt,
  FileText,
  Save,
  X,
} from "lucide-react";
import useFormData from "@/hooks/useFormData";
import { useGetExpenseCategoryQuery } from "@/store/ExpenseCategoryApi";
import {
  useAddExpanceMutation,
  useUpadteExpanceMutation,
  useDeleteExpanceMutation,
} from "@/store/expancesApi";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

interface Expense {
  ledger: string;
  remark?: string;
  amount: string;
  date: string;
}

const AddExpenseForm = ({ editExpanse = null, setEdit = () => {} }) => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const { data: allLedgers, isLoading: ledgersLoading } =
    useGetExpenseCategoryQuery(user?.token);

  const [addExpanseFn, { isSuccess: addSuccess }] = useAddExpanceMutation();
  const [updateExpanseFn, { isSuccess: updateSuccess }] =
    useUpadteExpanceMutation();

  // Format initial data for the form
  const initialData = React.useMemo(() => {
    if (editExpanse) {
      return {
        ledger: editExpanse.ledger._id,
        remark: editExpanse.remark,
        amount: editExpanse.amount.toString(),
        date: new Date(editExpanse.date).toISOString().split("T")[0],
      };
    }
    return {
      ledger: allLedgers?.find((option) =>
        option.ledger === category ? option._id : "",
      ),
      remark: "",
      amount: "",
      date: "",
    };
  }, [editExpanse]);

  React.useEffect(() => {
    if (editExpanse) {
      setEdit(editExpanse);
    }
    return () => {
      setEdit(null);
    };
  }, [editExpanse]);

  const { formData, handleChange, resetForm } =
    useFormData<Expense>(initialData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const refactoredData = {
      ...formData,
      date: new Date(formData.date).toLocaleDateString(),
      month: new Date(formData.date).getMonth() + 1,
      year: new Date(formData.date).getFullYear(),
    };

    if (editExpanse) {
      await updateExpanseFn({
        Token: user?.token,
        expanseId: editExpanse._id,
        data: refactoredData,
      });
    } else {
      await addExpanseFn({
        Token: user?.token,
        data: refactoredData,
      });
    }
  };

  React.useEffect(() => {
    if (addSuccess || updateSuccess) {
      resetForm();
      toast.success(
        editExpanse
          ? "Expense Updated Successfully"
          : "Expense Added Successfully",
      );
      navigate("/expenses");
    }
  }, [addSuccess, updateSuccess]);

  return (
    <Card className=" overflow-auto h-[80vh] rounded-none border-l-0">
      <CardHeader className="">
        <CardTitle className="text-2xl flex items-center gap-2">
          <Receipt className="w-6 h-6" />
          <h1>{editExpanse ? "Edit Expense" : "Add New Expense"}</h1>
        </CardTitle>
        <p className="">Enter expense details below</p>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ledger Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                <BookOpen className="h-4 w-4 text-red-600" />
                Ledger <span className="text-red-500">*</span>
              </Label>

              {category ? (
                <div className="p-2 border rounded-md bg-gray-50 dark:bg-gray-800 text-slate-500">
                  {allLedgers?.find((option) => option.ledger === category)
                    ?.ledger || "Ledger not found"}
                </div>
              ) : (
                <Select
                  value={formData.ledger}
                  required
                  onValueChange={(value) =>
                    handleChange({ id: "ledger", value })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Ledger" />
                  </SelectTrigger>
                  <SelectContent>
                    {allLedgers?.map((option) => (
                      <SelectItem key={option._id} value={option._id}>
                        {option.ledger}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-red-600" />
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1.5 text-gray-500">₹</span>
                <Input
                  type="text"
                  id="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                  className="w-full p-3 pl-8 border-gray-300 border rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Date Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2">
                <Calendar className="w-4 h-4 text-red-600" />
                Expense Date <span className="text-red-500">*</span>
              </label>
              <Input
                required
                type="date"
                id="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Remarks Textarea */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-600" />
              Remarks <span className="text-red-500">*</span>
            </label>
            <Textarea
              required
              id="remark"
              value={formData.remark}
              onChange={handleChange}
              rows={4}
              className="w-full p-3 border rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              placeholder="Enter expense details..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => resetForm()}
              className="px-6 py-3 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-all flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              Clear
            </button>

            <button
              type="submit"
              disabled={ledgersLoading}
              className={`px-6 py-3 rounded-lg text-white font-medium transition-all flex items-center gap-2
                ${
                  ledgersLoading ? "bg-red-400" : "bg-red-600 hover:bg-red-700"
                }`}
            >
              <Save className="w-5 h-5" />
              {ledgersLoading
                ? "Saving..."
                : editExpanse
                  ? "Update Expense"
                  : "Save Expense"}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddExpenseForm;
